import { routes } from "../generated/routes";
import { staticText } from "../statics/static-text";
import { stripOptionalParams, stripTrailingParams } from "../utils/route.utils";
import { AppId } from "./app-id";
import { HashedDynamicPermission, HashedPermission } from "./permissions";

export enum SubModuleType {
  LINK = "LINK",
  BACKBUTTON = "BACKBUTTON",
}

export type SubModule = {
  label: string;
  route: string;
  type?: SubModuleType;
  permissions?: (HashedPermission | HashedDynamicPermission)[];
  match?: "AnyPermission" | "AllPermissions";
  matchRoute?: string;
};

// Enum value is used to determine the order of sections in the user menu
export enum UserMenuSection {
  EXTRA_MODULES = 1,
  DEFAULT = 2,
}

type AppModule = {
  key: AppModuleKey;
  groupKey: string;
  permissions: (HashedPermission | HashedDynamicPermission)[];
  match: "AnyPermission" | "AllPermissions";
  baseRoute?: string;
  route?: string;
  label?: string;
  subModules?: SubModule[];
  dashboardOrder?: number;
  userMenuOrder?: number;
  userMenuSection?: UserMenuSection;
  /** Apps with a route for this module; every app when omitted */
  appIds?: AppId[];
};

export enum AppModuleKey {
  PORTAL = "portal",
  SOCIAL_CLEARANCES = "social-clearances",
  STATEMENTS = "statements",
  PAYOUTS = "payouts",
  RESOURCES = "resources",
  LICENSES = "licenses",
  MANAGE_TEAM = "manage-team",
  ORGANIZATIONS = "organizations",
  MANAGE_ORGANIZATION = "manage-organization",
  CREATIVE_SERVICES = "creative-services",
  HELP_CENTER = "help-center",
  CUSTOMER_SUPPORT = "customer-support",
  MUSIC = "music",
  PROFILE = "profile",
  SIGN_OUT = "sign-out",
  LABS = "labs",
  MUSIC_INGESTION = "music-ingestion",
  LEGAL = "legal",
  DISTRIBUTION = "distribution",
  REPORTING = "reporting",
  STATEMENT_IMPORTS = "statement-imports",
  PAYOUT_ADMINISTRATION = "payout-administration",
}

export const appModules: Record<AppModuleKey, AppModule> = {
  [AppModuleKey.PORTAL]: {
    key: AppModuleKey.PORTAL,
    baseRoute: routes.portal.base,
    groupKey: "app-modules",
    permissions: [],
    match: "AnyPermission",
  },
  [AppModuleKey.SOCIAL_CLEARANCES]: {
    key: AppModuleKey.SOCIAL_CLEARANCES,
    baseRoute: routes.socialClearances.base,
    groupKey: "app-modules",
    permissions: [
      HashedPermission.SOCIAL_CLEARANCES,
      HashedPermission.SOCIAL_CLEARANCES_CREATE,
    ],
    match: "AnyPermission",
    label: staticText.modules.socialClearances,
    dashboardOrder: 1,
  },
  [AppModuleKey.STATEMENTS]: {
    key: AppModuleKey.STATEMENTS,
    baseRoute: routes.statements.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.STATEMENTS],
    match: "AnyPermission",
    label: staticText.modules.statements,
    dashboardOrder: 2,
  },
  [AppModuleKey.PAYOUTS]: {
    key: AppModuleKey.PAYOUTS,
    baseRoute: routes.payouts.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.PAYOUTS],
    match: "AnyPermission",
    label: staticText.modules.payouts,
    dashboardOrder: 3,
  },
  [AppModuleKey.RESOURCES]: {
    key: AppModuleKey.RESOURCES,
    baseRoute: routes.resources.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.RESOURCES],
    match: "AnyPermission",
    dashboardOrder: 3,
    label: staticText.modules.resources,
  },
  [AppModuleKey.LICENSES]: {
    key: AppModuleKey.LICENSES,
    baseRoute: routes.licenses.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.LICENSES],
    match: "AnyPermission",
    dashboardOrder: 4,
    label: staticText.modules.licenses.label,
  },
  [AppModuleKey.MANAGE_TEAM]: {
    key: AppModuleKey.MANAGE_TEAM,
    baseRoute: routes.orgs.users,
    groupKey: "settings",
    permissions: [
      HashedPermission.ORGS_USERS,
      HashedPermission.ORGS_USERS_INVITE,
    ],
    match: "AnyPermission",
    label: staticText.modules.manageTeam,
    userMenuOrder: 2,
    userMenuSection: UserMenuSection.DEFAULT,
  },
  [AppModuleKey.ORGANIZATIONS]: {
    key: AppModuleKey.ORGANIZATIONS,
    baseRoute: routes.orgs.base,
    groupKey: "settings",
    permissions: [HashedDynamicPermission.MULTI_ORG_ACCESS],
    match: "AnyPermission",
    label: staticText.modules.organizations,
    userMenuOrder: 3,
    userMenuSection: UserMenuSection.DEFAULT,
  },
  [AppModuleKey.MANAGE_ORGANIZATION]: {
    /** @todo PJ: limit acceptable permissions */
    key: AppModuleKey.MANAGE_ORGANIZATION,
    baseRoute: routes.orgs.settings,
    groupKey: "settings",
    permissions: [
      HashedPermission.ORGS_USERS,
      HashedPermission.ORGS_USERS_INVITE,
      HashedPermission.ORGS_SETTINGS,
      HashedPermission.PAYOUTS,
    ],
    match: "AnyPermission",
  },
  [AppModuleKey.CREATIVE_SERVICES]: {
    key: AppModuleKey.CREATIVE_SERVICES,
    baseRoute: routes.portal.base,
    groupKey: "settings",
    permissions: [],
    match: "AnyPermission",
  },
  [AppModuleKey.HELP_CENTER]: {
    key: AppModuleKey.HELP_CENTER,
    groupKey: "settings",
    permissions: [HashedPermission.SYSTEM_ROOT],
    match: "AnyPermission",
    label: staticText.modules.helpCenter,
    userMenuOrder: 4,
    userMenuSection: UserMenuSection.DEFAULT,
  },
  [AppModuleKey.CUSTOMER_SUPPORT]: {
    key: AppModuleKey.CUSTOMER_SUPPORT,
    baseRoute: routes.portal.customerSupport,
    groupKey: "settings",
    permissions: [HashedPermission.CUSTOMER_SUPPORT],
    match: "AnyPermission",
    dashboardOrder: 5,
    label: staticText.modules.customerSupport,
  },
  [AppModuleKey.MUSIC]: {
    key: AppModuleKey.MUSIC,
    baseRoute: routes.music.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.MUSIC],
    match: "AnyPermission",
    label: staticText.modules.music.label,
    dashboardOrder: 1,
    subModules: [
      {
        label: staticText.modules.music.discover,
        route: routes.music.discover.base,
      },
      {
        label: staticText.modules.music.library,
        route: routes.music.library.base,
      },
      {
        label: staticText.modules.music.connect,
        route: routes.music.connect.base,
      },
    ],
  },
  [AppModuleKey.LABS]: {
    key: AppModuleKey.LABS,
    baseRoute: routes.labs.base,
    route: stripOptionalParams(routes.labs.apps),
    groupKey: "app-modules",
    permissions: [
      HashedDynamicPermission.LABS_APP_ACCESS,
      HashedPermission.LABS_ADMIN,
      HashedPermission.LABS_DEVELOPER,
    ],
    match: "AnyPermission",
    label: staticText.modules.labs.label,
    dashboardOrder: 4,
    subModules: [
      {
        label: staticText.modules.labs.backButton,
        route: stripOptionalParams(routes.labs.apps),
        matchRoute: `${stripTrailingParams(routes.labs.app)}/`,
        type: SubModuleType.BACKBUTTON,
      },
    ],
  },
  [AppModuleKey.MUSIC_INGESTION]: {
    key: AppModuleKey.MUSIC_INGESTION,
    baseRoute: routes.musicIngestion.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.MUSIC_INGESTION_ACCESS],
    match: "AnyPermission",
    label: staticText.modules.musicIngestion,
    userMenuOrder: 1,
    userMenuSection: UserMenuSection.EXTRA_MODULES,
  },
  [AppModuleKey.STATEMENT_IMPORTS]: {
    key: AppModuleKey.STATEMENT_IMPORTS,
    baseRoute: routes.statementImports.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.SYSTEM_ROOT],
    match: "AnyPermission",
    label: staticText.modules.statementImports,
    userMenuOrder: 4,
    userMenuSection: UserMenuSection.EXTRA_MODULES,
    // Only stone-frontend routes /statement-imports
    appIds: [AppId.SongtradrOne],
  },
  [AppModuleKey.PAYOUT_ADMINISTRATION]: {
    key: AppModuleKey.PAYOUT_ADMINISTRATION,
    baseRoute: routes.payoutAdministration.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.SYSTEM_ROOT],
    match: "AnyPermission",
    label: staticText.modules.payoutAdministration,
    userMenuOrder: 5,
    userMenuSection: UserMenuSection.EXTRA_MODULES,
    // Only stone-frontend routes /payout-administration
    appIds: [AppId.SongtradrOne],
  },
  [AppModuleKey.PROFILE]: {
    key: AppModuleKey.PROFILE,
    groupKey: "settings",
    permissions: [],
    match: "AnyPermission",
    label: staticText.modules.profile,
    userMenuOrder: 1,
    userMenuSection: UserMenuSection.DEFAULT,
  },
  [AppModuleKey.SIGN_OUT]: {
    key: AppModuleKey.SIGN_OUT,
    groupKey: "settings",
    permissions: [],
    match: "AnyPermission",
    label: staticText.modules.signOut,
    userMenuOrder: 5,
    userMenuSection: UserMenuSection.DEFAULT,
  },
  [AppModuleKey.LEGAL]: {
    key: AppModuleKey.LEGAL,
    baseRoute: routes.legal.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.LEGAL_ADMIN],
    match: "AnyPermission",
    label: staticText.modules.legal,
    userMenuOrder: 2,
    userMenuSection: UserMenuSection.EXTRA_MODULES,
  },
  [AppModuleKey.DISTRIBUTION]: {
    key: AppModuleKey.DISTRIBUTION,
    baseRoute: routes.distribution.base,
    groupKey: "app-modules",
    permissions: [HashedPermission.DISTRIBUTION_ACCESS],
    match: "AnyPermission",
    label: staticText.modules.distribution,
    dashboardOrder: 6,
  },
  [AppModuleKey.REPORTING]: {
    key: AppModuleKey.REPORTING,
    baseRoute: routes.reporting.base,
    groupKey: "app-modules",
    // Root only until the dedicated REPORTING_ACCESS permission is rolled out to SSO
    permissions: [HashedPermission.SYSTEM_ROOT],
    match: "AnyPermission",
    label: staticText.modules.reporting,
    userMenuOrder: 3,
    userMenuSection: UserMenuSection.EXTRA_MODULES,
  },
};

// Export as a list for easier iteration and sort DESC by baseRoute length for AppHeader matching location.pathname.startsWith(going longest to shortest)
export const appModulesList = Object.values(appModules).sort(
  (a, b) => (b.baseRoute?.length ?? 0) - (a.baseRoute?.length ?? 0),
);
